export const PERSON_RELATION_TYPES = [
  'mother',
  'father',
  'parent',
  'child',
  'son',
  'daughter',
  'husband',
  'wife',
  'spouse',
  'partner',
  'sister',
  'brother',
  'sibling',
  'grandmother',
  'grandfather',
  'grandparent',
  'grandchild',
  'guardian',
  'ward',
  'other',
] as const;

export const ORG_RELATION_TYPES = [
  'insurance',
  'hospital',
  'external_practitioner',
  'employer',
  'municipality',
  'other_organization',
] as const;

export const CLINIC_CONTACT_KINDS = [
  'insurance',
  'hospital',
  'external_practitioner',
  'employer',
  'municipality',
  'other',
] as const;

export type PersonRelationType = (typeof PERSON_RELATION_TYPES)[number];
export type OrgRelationType = (typeof ORG_RELATION_TYPES)[number];
export type ClinicContactKind = (typeof CLINIC_CONTACT_KINDS)[number];

/**
 * Sex implied by a person-relation type. There is no separate gender table:
 * the type names already say it (Mor, Far, Søn, Datter, …). Neutral types
 * (Forælder, Partner, Andet, …) have no required sex.
 *
 * A person's sex is the CPR sex (`sexFromCpr`: 10th digit odd = male, even =
 * female). Missing CPR and a replacement CPR do not encode that digit, so
 * sex is unknown. A gendered list keeps the matching sex and unknown sex.
 * Only a known sex that conflicts with the relation is left out.
 */
export type RelationSex = 'male' | 'female';

const PERSON_RELATION_SEX: Record<PersonRelationType, RelationSex | null> = {
  mother: 'female',
  father: 'male',
  parent: null,
  child: null,
  son: 'male',
  daughter: 'female',
  husband: 'male',
  wife: 'female',
  spouse: null,
  partner: null,
  sister: 'female',
  brother: 'male',
  sibling: null,
  grandmother: 'female',
  grandfather: 'male',
  grandparent: null,
  grandchild: null,
  guardian: null,
  ward: null,
  other: null,
};

function isPersonRelationType(value: string): value is PersonRelationType {
  return (PERSON_RELATION_TYPES as readonly string[]).includes(value);
}

/** Required sex for this relation type, or null when any person may be chosen. */
export function expectedSexForPersonRelation(relationType: string): RelationSex | null {
  if (!isPersonRelationType(relationType)) return null;
  return PERSON_RELATION_SEX[relationType];
}

/**
 * Gendered types keep a matching CPR sex and an unknown sex (null).
 * A known sex that conflicts is excluded. Neutral types match everyone.
 */
export function personMatchesRelationSex(
  personSex: RelationSex | null | undefined,
  relationType: string,
): boolean {
  const expected = expectedSexForPersonRelation(relationType);
  if (!expected) return true;
  if (personSex == null) return true;
  return personSex === expected;
}

/**
 * Gender filter applies to a fresh search only. A person the user already
 * chose stays in the picker when a later relation would have hidden them.
 */
export function keepSelectedRelationPerson<T extends { id: string }>(
  results: readonly T[],
  selected: T | null,
): T[] {
  if (!selected || results.some((row) => row.id === selected.id)) return [...results];
  return [selected, ...results];
}

/** Maps org relation type to the clinic_contacts.kind used when creating inline. */
export function contactKindFromOrgRelation(relationType: OrgRelationType): ClinicContactKind {
  if (relationType === 'other_organization') return 'other';
  return relationType;
}
